import { Icon, List } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Spin from 'components/Spin';
import React, { useCallback, useState } from 'react';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import { useObservable, useRetryableObservable } from 'react-use-observable';
import MyModal from 'v2/components/Shared/MyModal';
import chargebackService from 'v2/services/chargeback';
import uploadService from 'v2/services/upload';

import ChargebackDetailsListItem from './ListItem';

interface IProps {
  visible: boolean;
  invoiceId: number;
  retryLoadChargeback: () => void;
  setModalVisible: (visible: boolean, invoiceId: number | undefined) => void;
}

const useStyle = makeStyles(() => ({
  dropzone: {
    display: 'flex',
    width: '100%',
    borderStyle: 'dashed',
    padding: 16,
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  icon: {
    marginRight: 16,
  },
  list: {
    maxHeight: '350px',
    overflowY: 'auto',
  },
}));

const ChargebackDetails = ({ setModalVisible, visible, invoiceId, retryLoadChargeback }: IProps) => {
  const classes = useStyle({});

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [hasUploaded, setHasUploded] = useState(false);
  const [chargebackFiles] = useObservable(() => chargebackService.getFiles(), []);

  const [isLoading] = useObservable(() => chargebackService.isLoadingFiles(), []);

  const [, , , retry] = useRetryableObservable(() => chargebackService.loadChargebacksFiles(invoiceId), [invoiceId]);

  const setUpload = useCallback(() => setHasUploded(true), []);

  const handleClose = useCallback(() => {
    if (hasUploaded) {
      retryLoadChargeback();
    }
    setModalVisible(false, undefined);
  }, [hasUploaded, retryLoadChargeback, setModalVisible]);

  const onDrop = async (filesReceived: File[]) => {
    setLoadingSubmit(true);
    try {
      await Promise.all(
        filesReceived.map(async (file: File) => {
          await uploadService.uploadFile(file, 'prova_acesso', null, null, invoiceId);
        })
      );
      toast.success('Arquivo(s) adicionado(s) com sucesso!');
      setUpload();
      setLoadingSubmit(false);
      retry();
    } catch (error) {
      setLoadingSubmit(false);
      toast.error(error);
    }
  };

  const content = () => {
    return (
      <div>
        {loadingSubmit ? (
          <div>
            <Spin />
            <p>Por favor aguarde, estamos fazendo upload do(s) arquivo(s) ...</p>
          </div>
        ) : (
          <div>
            <Dropzone onDrop={onDrop}>
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div className={classes.dropzone} {...getRootProps()}>
                    <input {...getInputProps()} />
                    <Icon className={classes.icon}>backup</Icon>
                    <div>
                      <p>Clique ou arraste os arquivos nessa área.</p>
                      <p>Pode ser selecionado vários arquivos ao mesmo tempo!</p>
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
            <div className='m16' />
          </div>
        )}
      </div>
    );
  };

  if (!visible && invoiceId === undefined) return null;

  return (
    <MyModal title='Provas de acesso ao conteúdo' opened={visible} showOk={false} buttonCancel='Fechar' onClose={handleClose} submitDisabled={true}>
      {content()}
      <div className='m16' />
      {isLoading ? (
        <div>
          <Spin />
          <p>Carregando arquivo(s) ...</p>
        </div>
      ) : (
        <List className={classes.list}>
          {chargebackFiles?.map((item) => (
            <ChargebackDetailsListItem key={item.id} setUpload={setUpload} invoiceId={invoiceId} data={item} />
          ))}
        </List>
      )}
    </MyModal>
  );
};

export default ChargebackDetails;
